import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const ROLE_BADGE = { seller:'badge--green', buyer:'badge--blue', agent:'badge--purple', admin:'badge--danger' };
const ROLE_LABEL = { seller:'Farmer', buyer:'Buyer', agent:'Agent', admin:'Admin' };

const DATE_FILTERS = [
  { label:'All time',  value:'all' },
  { label:'Today',     value:'today' },
  { label:'Yesterday', value:'yesterday' },
  { label:'1 Week',    value:'7' },
  { label:'15 Days',   value:'15' },
  { label:'1 Month',   value:'30' },
  { label:'3 Months',  value:'90' },
  { label:'6 Months',  value:'180' },
  { label:'Custom',    value:'custom' },
];

function getDateRange(filter, customFrom, customTo) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (filter === 'all')       return {};
  if (filter === 'today')     return { from: today.toISOString() };
  if (filter === 'yesterday') {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    const e = new Date(today); e.setMilliseconds(-1);
    return { from: y.toISOString(), to: e.toISOString() };
  }
  if (filter === 'custom') return { from: customFrom, to: customTo };
  const days = parseInt(filter);
  const from = new Date(today); from.setDate(from.getDate() - days);
  return { from: from.toISOString() };
}

// ── User detail modal with tabs ───────────────────────────────
function UserModal({ user, onClose, onToggleBan }) {
  const [tab, setTab]             = useState('profile');
  const [referrals, setReferrals] = useState([]);
  const [referredBy, setReferredBy] = useState(null);
  const [refLoading, setRefLoading] = useState(false);

  useEffect(() => {
    if (tab === 'referrals') loadReferrals();
  }, [tab]);

  const loadReferrals = async () => {
    setRefLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${user._id}/referrals`);
      setReferrals(data.referrals || []);
      setReferredBy(data.referredBy || null);
    } catch { toast.error('Failed to load referrals'); }
    finally { setRefLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth:520 }} onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <div className="modal__title">👤 {user.name || 'User Profile'}</div>
          <button className="modal__close" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:0, borderBottom:'1px solid #E2E8F0', padding:'0 20px' }}>
          {['profile', 'referrals'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:'10px 16px', border:'none', background:'none', cursor:'pointer',
                fontSize:13, fontWeight:600, textTransform:'capitalize',
                color: tab===t ? '#1A6B3C' : '#64748B',
                borderBottom: tab===t ? '2px solid #1A6B3C' : '2px solid transparent' }}>
              {t === 'profile' ? '👤 Profile' : `🔗 Referrals (${user.referralCount || 0})`}
            </button>
          ))}
        </div>

        <div className="modal__body">
          {/* ── Profile tab ── */}
          {tab === 'profile' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[
                ['Mobile',        '+91 ' + user.mobile],
                ['Role',          ROLE_LABEL[user.role]||user.role],
                ['Location',      user.locationAddress||'—'],
                ['Gender',        user.gender||'—'],
                ['Referral Code', user.referralCode||'—'],
                ['Referrals',     user.referralCount||0],
                ['Star Level',    user.starLevel||0],
                ['Crops',         user.cropCount||0],
                ['Status',        user.isActive ? 'Active' : 'Banned'],
                ['Joined',        new Date(user.createdAt).toLocaleDateString('en-IN')],
              ].map(([k,v]) => (
                <div key={k} style={{ background:'#f8f9fa', borderRadius:6, padding:'10px 12px' }}>
                  <div style={{ fontSize:11, color:'#6C757D', marginBottom:2 }}>{k}</div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{String(v)}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Referrals tab ── */}
          {tab === 'referrals' && (
            <div>
              {/* Referred by */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#64748B', marginBottom:8 }}>
                  REFERRED BY
                </div>
                {referredBy ? (
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'#F0FDF4', borderRadius:8, border:'1px solid #BBF7D0' }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', background:'#1A6B3C', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14 }}>
                      {referredBy.name?.[0] || '?'}
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{referredBy.name || '—'}</div>
                      <div style={{ fontSize:11, color:'#64748B' }}>+91 {referredBy.mobile} · {ROLE_LABEL[referredBy.role]}</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize:13, color:'#94A3B8', padding:'8px 0' }}>No referrer — joined directly</div>
                )}
              </div>

              {/* Users referred by this person */}
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'#64748B', marginBottom:8 }}>
                  USERS REFERRED BY {(user.name || 'THIS USER').toUpperCase()} ({referrals.length})
                </div>
                {refLoading ? (
                  <div style={{ textAlign:'center', padding:20 }}><span className="spinner spinner--sm" /></div>
                ) : referrals.length === 0 ? (
                  <div style={{ fontSize:13, color:'#94A3B8', padding:'8px 0' }}>No referrals yet</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {referrals.map(r => (
                      <div key={r._id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'#f8f9fa', borderRadius:8, border:'1px solid #E2E8F0' }}>
                        <div style={{ width:30, height:30, borderRadius:'50%', background:'#3B82F6', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13 }}>
                          {r.name?.[0] || '?'}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600 }}>{r.name || '—'}</div>
                          <div style={{ fontSize:11, color:'#64748B' }}>
                            +91 {r.mobile} · {ROLE_LABEL[r.role]}
                          </div>
                        </div>
                        <div style={{ fontSize:11, color:'#94A3B8' }}>
                          {new Date(r.createdAt).toLocaleDateString('en-IN')}
                        </div>
                        <span className={'badge ' + (r.isActive ? 'badge--success' : 'badge--danger')}>
                          {r.isActive ? 'Active' : 'Banned'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal__footer">
          <button className="btn btn--outline" onClick={onClose}>Close</button>
          <button className={'btn ' + (user.isActive ? 'btn--danger' : 'btn--success')}
            onClick={() => { onToggleBan(user); onClose(); }}>
            {user.isActive ? 'Ban User' : 'Activate User'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UsersPage({ roleFilter }) {
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [role,       setRole]       = useState(roleFilter || '');
  const [page,       setPage]       = useState(1);
  const [total,      setTotal]      = useState(0);
  const [selected,   setSelected]   = useState(null);
  const [dateFilter, setDateFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [exporting,  setExporting]  = useState(false);

  useEffect(() => {
    setRole(roleFilter || '');
    setSearch('');
    setPage(1);
    setDateFilter('all');
    setShowCustom(false);
  }, [roleFilter]);

  useEffect(() => { load(); }, [role, search, page, dateFilter, customFrom, customTo]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit:20 });
      if (role)   params.set('role', role);
      if (search) params.set('search', search);
      const range = getDateRange(dateFilter, customFrom, customTo);
      if (range.from) params.set('from', range.from);
      if (range.to)   params.set('to',   range.to);
      const { data } = await api.get('/admin/users?' + params);
      setUsers(data.users || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const handleDateFilter = (val) => {
    setDateFilter(val);
    setShowCustom(val === 'custom');
    setPage(1);
  };

  const toggleBan = async (user) => {
    try {
      await api.put('/admin/users/' + user._id + '/toggle');
      setUsers(p => p.map(u => u._id === user._id ? { ...u, isActive:!u.isActive } : u));
      toast.success(user.isActive ? 'User banned' : 'User activated');
    } catch { toast.error('Error'); }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ limit:1000 });
      if (role) params.set('role', role);
      if (search) params.set('search', search);
      const range = getDateRange(dateFilter, customFrom, customTo);
      if (range.from) params.set('from', range.from);
      if (range.to)   params.set('to',   range.to);
      const { data } = await api.get('/admin/users?' + params);
      const rows = (data.users || []).map(u => ({
        Name:      u.name || '—',
        Mobile:    '+91 ' + u.mobile,
        Role:      ROLE_LABEL[u.role] || u.role,
        Location:  u.locationAddress || '—',
        Crops:     u.cropCount || 0,
        Referrals: u.referralCount || 0,
        Status:    u.isActive ? 'Active' : 'Banned',
        Joined:    new Date(u.createdAt).toLocaleDateString('en-IN'),
      }));
      if (!rows.length) return toast.error('No data');
      const headers = Object.keys(rows[0]);
      const csv = [headers.join(','), ...rows.map(r => headers.map(h => '"' + String(r[h]).replace(/"/g,'""') + '"').join(','))].join('\r\n');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type:'text/csv' }));
      a.download = title.toLowerCase().replace(' ','_') + '_' + Date.now() + '.csv';
      a.click();
      toast.success('Exported ' + rows.length + ' users');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const deleteUser = async (user) => {
    if (!window.confirm('Permanently delete "' + (user.name||user.mobile) + '"? This cannot be undone.')) return;
    try {
      await api.delete('/admin/users/' + user._id);
      setUsers(p => p.filter(u => u._id !== user._id));
      setTotal(p => p - 1);
      toast.success('User deleted');
    } catch { toast.error('Error deleting user'); }
  };

  const title = roleFilter === 'seller' ? 'Farmers'
              : roleFilter === 'buyer'  ? 'Buyers'
              : roleFilter === 'agent'  ? 'Agents'
              : 'All Users';

  const activeCount = users.filter(u => u.isActive).length;
  const bannedCount = users.filter(u => !u.isActive).length;

  return (
    <AdminLayout title={title}>
      {/* Date filter pills */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
        {DATE_FILTERS.map(f => (
          <button key={f.value} onClick={() => handleDateFilter(f.value)}
            style={{ padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:500, cursor:'pointer',
              border:'1.5px solid ' + (dateFilter===f.value ? '#1A6B3C' : '#dee2e6'),
              background: dateFilter===f.value ? '#1A6B3C' : 'white',
              color: dateFilter===f.value ? 'white' : '#6C757D' }}>
            {f.label}
          </button>
        ))}
      </div>

      {showCustom && (
        <div style={{ display:'flex', gap:10, marginBottom:12, padding:'12px 14px', background:'#f8f9fa', borderRadius:8, border:'1px solid #dee2e6', flexWrap:'wrap' }}>
          <div>
            <label style={{ fontSize:11, color:'#6C757D', display:'block', marginBottom:3 }}>From date</label>
            <input type="date" className="input" value={customFrom} onChange={e => { setCustomFrom(e.target.value); setPage(1); }} style={{ width:150 }} />
          </div>
          <div>
            <label style={{ fontSize:11, color:'#6C757D', display:'block', marginBottom:3 }}>To date</label>
            <input type="date" className="input" value={customTo} onChange={e => { setCustomTo(e.target.value); setPage(1); }} style={{ width:150 }} />
          </div>
          <div style={{ alignSelf:'flex-end' }}>
            <button className="btn btn--primary btn--sm" onClick={load}>Apply</button>
          </div>
        </div>
      )}

      <div className="flex-between" style={{ marginBottom:12 }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <input className="input" placeholder="Search name, mobile..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ width:220 }} />
          {!roleFilter && (
            <select className="input" value={role} onChange={e => { setRole(e.target.value); setPage(1); }} style={{ width:140 }}>
              <option value="">All Roles</option>
              <option value="seller">Farmers</option>
              <option value="buyer">Buyers</option>
              <option value="agent">Agents</option>
            </select>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ fontSize:12, color:'#6C757D' }}>
            <span style={{ color:'#16A34A', fontWeight:600 }}>{activeCount} active</span>
            {bannedCount > 0 && <span style={{ color:'#DC2626', fontWeight:600, marginLeft:6 }}>{bannedCount} banned</span>}
            <span style={{ marginLeft:6 }}>· {total} total</span>
          </div>
          <button className="btn btn--outline btn--sm" onClick={exportCSV} disabled={exporting}>
            {exporting ? <span className="spinner spinner--sm" /> : '📊 Export CSV'}
          </button>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="page-loader"><span className="spinner spinner--md" /></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Name</th><th>Mobile</th><th>Role</th><th>Location</th>
                <th>Crops</th><th>Referrals</th><th>Joined</th><th>Status</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={9}>
                    <div className="empty-state">
                      <div className="empty-state__icon">👥</div>
                      <div className="empty-state__title">No {title.toLowerCase()} found</div>
                      <div className="empty-state__desc">Try adjusting the date filter or search</div>
                    </div>
                  </td></tr>
                ) : users.map(u => (
                  <tr key={u._id}>
                    {/* <td><div style={{ fontWeight:600, fontSize:13 }}>{u.name || '—'}</div></td> */}

                    <td>
  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
    <div style={{ width:34, height:34, borderRadius:'50%', background:'#1A6B3C', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14, flexShrink:0 }}>
      {u.name?.[0]?.toUpperCase() || '?'}
    </div>
    <div style={{ fontWeight:600, fontSize:13 }}>{u.name || '—'}</div>
  </div>
</td>
                    <td style={{ color:'#2563EB', fontSize:13 }}>+91 {u.mobile}</td>
                    <td><span className={'badge ' + (ROLE_BADGE[u.role]||'badge--gray')}>{ROLE_LABEL[u.role]||u.role}</span></td>
                    <td style={{ fontSize:12, color:'#6C757D' }}>{u.locationAddress?.split(',')[0] || '—'}</td>
                    <td style={{ textAlign:'center' }}>{u.cropCount || 0}</td>
                    <td style={{ textAlign:'center' }}>
                      {u.referralCount > 0
                        ? <span style={{ background:'#F0FDF4', color:'#16A34A', borderRadius:12, padding:'2px 8px', fontSize:12, fontWeight:600 }}>🔗 {u.referralCount}</span>
                        : <span style={{ color:'#94A3B8', fontSize:12 }}>0</span>}
                    </td>
                    <td style={{ fontSize:12 }}>{new Date(u.createdAt).toLocaleDateString('en-IN')}</td>
                    <td><span className={'badge ' + (u.isActive?'badge--success':'badge--danger')}>{u.isActive?'Active':'Banned'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn--outline btn--sm" onClick={() => setSelected(u)}>👁 View</button>
                        <button className={'btn btn--sm ' + (u.isActive?'btn--danger':'btn--success')} onClick={() => toggleBan(u)}>
                          {u.isActive ? 'Ban' : 'Activate'}
                        </button>
                        <button className="btn btn--danger btn--sm" onClick={() => deleteUser(u)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > 20 && (
        <div className="pagination">
          <button className="btn btn--outline btn--sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
          <span className="page-info">Page {page} of {Math.ceil(total/20)} · {total} users</span>
          <button className="btn btn--outline btn--sm" disabled={page>=Math.ceil(total/20)} onClick={() => setPage(p=>p+1)}>Next →</button>
        </div>
      )}

      {selected && (
        <UserModal
          user={selected}
          onClose={() => setSelected(null)}
          onToggleBan={toggleBan}
        />
      )}
    </AdminLayout>
  );
}